<template>
  <div class="flex flex-row justify-between">
    <TemplateDropdown
      v-model:selected-template="selectedTemplate"
      :templates
      option-label="templateName"
      size="w-60"
      show-save
      show-delete
      :delete-disabled="selectedTemplate == null"
      @selected="applyTemplate"
      @save="showSaveTemplate"
      @delete="tryDeleteTemplate"
    ></TemplateDropdown>
    <FileUploadButton v-model:file="file" size="w-60"></FileUploadButton>
  </div>

  <DynamicTabs v-model:active-tab="activeTab" v-model:tabs="tabs" class="w-fit!"></DynamicTabs>
  <Card pt:body:class="p-5! pt-0!" pt:content:class="py-0!">
    <template #content>
      <div class="min-h-[490px] content-center">
        <ProjectInfoImport
          v-if="activeTab === 0 && tbdEntity"
          v-model:import-data="projectData"
          v-model:field-data="projectFieldData"
          v-model:entities="entities"
          :projectData
          :tbdEntity
          :userGroups
          :file
        ></ProjectInfoImport>

        <BudgetInfoImport
          v-if="activeTab === 1"
          v-model:import-data="budgetData"
          v-model:field-data="budgetFieldData"
          :file
          initial-budget
        ></BudgetInfoImport>
      </div>
    </template>
  </Card>
  <div class="flex flex-row justify-between mt-5">
    <CancelButton label="Reset" icon="refresh" @click="resetImport"></CancelButton>
    <SubmitButton label="Import" icon="file-import" @click="importProject"></SubmitButton>
  </div>
  <TSDialog label="Save Template" v-model:visible="saveTemplateActive" icon="save" size="w-[600px]!">
    <template #content>
      <div class="flex flex-col gap-2 py-5">
        <label for="templateName" class="font-bold">Template Name:</label>
        <InputText name="templateName" type="text" class="h-10" v-model="templateName" autofocus v-on:keyup.enter="trySaveTemplate" />
        <SubmitButton label="Save Template" :showIcon="false" @click="trySaveTemplate"></SubmitButton>
      </div>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { getAllEntities } from "@/services/EntitiesService";
import { entityIsTBD, getUserGroups, toFormData2 } from "@/services/Utility";
import type { Code } from "@/types/Codes";
import type { Entity } from "@/types/Entity";
import {
  ImportBudgetFieldDataSchema,
  ImportBudgetSchema,
  ImportProjectFieldDataSchema,
  ImportProjectSchema,
  ImportProjectTemplateListSchema,
  type ImportBudget,
  type ImportBudgetFieldData,
  type ImportProject,
  type ImportProjectTemplate,
} from "@/types/Import";
import type { UserDetail } from "@/types/User";
import type { MenuItem } from "primevue/menuitem";
import { setBudgetImportInfo, setProjectImportInfo } from "@/services/ImportService";
import { useConfirm } from "primevue/useconfirm";

const confirm = useConfirm();
const router = useRouter();

interface Props {
  userDetail: UserDetail;
}

const props = defineProps<Props>();

const templates = ref<ImportProjectTemplate[]>([]);
const selectedTemplate = ref<ImportProjectTemplate>();
const saveTemplateActive = ref(false);
const templateName = ref("");
const file = ref<File | null>(null);

const entities = ref<Entity[]>([]);
const tbdEntity = ref<Entity>();

const userGroups = ref<Code[]>([]);

const budgetData = ref<ImportBudget>(ImportBudgetSchema.parse({}));
const budgetFieldData = ref<ImportBudgetFieldData>(ImportBudgetFieldDataSchema.parse({}));
const projectFieldData = ref<any>(ImportProjectFieldDataSchema.parse({}));
const projectData = ref<ImportProject>(ImportProjectSchema.parse({}));

//Initializes the budget tab
const activeTab = ref(0);
const tabs = ref<MenuItem[]>([
  {
    label: "Project",
    icon: "",
    visible: true,
    key: "project",
  },
  {
    label: "Budget",
    icon: "",
    visible: true,
    key: "budget",
  },
]);

const showSaveTemplate = (): void => {
  templateName.value = selectedTemplate.value != null ? selectedTemplate.value.templateName : "";
  saveTemplateActive.value = true;
};

const trySaveTemplate = (): void => {
  const existingTemplateIndex = templates.value.findIndex((template) => template.templateName === templateName.value);
  if (existingTemplateIndex !== -1) {
    confirmPopup(
      confirm,
      "Overwrite Existing Template",
      `A Template with the name ${templateName.value} already exists, do you want to overwrite it?`,
      () => {
        saveTemplate(existingTemplateIndex, templates.value[existingTemplateIndex].templateId);
      },
      () => info("Save Cancelled", "")
    );
  } else {
    saveTemplate(-1, "");
  }
};

const convertProjectInfoSelection = (tab: number, row: number, col: string): string => {
  return `${tab}:${row}:${col}`;
};

const saveTemplate = (existingTemplateIndex: number, existingTemplateId: string): void => {
  if (projectData.value.borrower == null) {
    error("Invalid Import", "Borrower may not be empty");
    return;
  }
  if (projectData.value.builder == null) {
    error("Invalid Import", "Builder may not be empty");
    return;
  }
  if (projectData.value.inspector == null) {
    error("Invalid Import", "Inspector may not be empty");
    return;
  }

  const data = projectFieldData.value;

  const template: ImportProjectTemplate = {
    templateId: existingTemplateId,
    borrower: projectData.value.borrower,
    builder: projectData.value.builder,
    inspector: projectData.value.inspector,
    projectId: convertProjectInfoSelection(data.projectIdTab, data.projectIdRow, data.projectIdCol),
    projectName: convertProjectInfoSelection(data.projectNameTab, data.projectNameRow, data.projectNameCol),
    projectAddress: convertProjectInfoSelection(data.projectAddressTab, data.projectAddressRow, data.projectAddressCol),
    projectNotes: convertProjectInfoSelection(data.projectNotesTab, data.projectNotesRow, data.projectNotesCol),
    projectInitLenderFunds: convertProjectInfoSelection(data.initialLenderFundsTab, data.initialLenderFundsRow, data.initialLenderFundsCol),
    projectAdjLenderFunds: convertProjectInfoSelection(data.adjustedLenderFundsTab, data.adjustedLenderFundsRow, data.adjustedLenderFundsCol),
    projectInitEquity: convertProjectInfoSelection(data.initialOtherEquityTab, data.initialOtherEquityRow, data.initialOtherEquityCol),
    projectAdjEquity: convertProjectInfoSelection(data.adjustedOtherEquityTab, data.adjustedOtherEquityRow, data.adjustedOtherEquityCol),
    budgetTab: budgetData.value.tab,
    budgetStartRow: budgetData.value.startRow,
    budgetEndRow: budgetData.value.endRow,
    budgetLineRef: budgetFieldData.value.lineRefCol,
    budgetDescription: budgetFieldData.value.descriptionCol,
    budgetAmount: budgetFieldData.value.budgetAmtCol,
    budgetPriorDrawnToDate: budgetFieldData.value.priorDrawnToDateCol,
    budgetThisDrawAmount: budgetFieldData.value.thisDrawAmtCol,
    budgetInspPriorDraw: budgetFieldData.value.inspPriorDrawCol,
    budgetInspThisDraw: budgetFieldData.value.inspThisDrawCol,
    budgetDefaultCostType: budgetData.value.defaultCostType,
    budgetPriorEquityDrawnToDate: budgetFieldData.value.priorEquityDrawnToDateCol,
    budgetEquityThisDrawAmount: budgetFieldData.value.thisEquityDrawAmtCol,
    initialBudget: budgetFieldData.value.initBudgetAmtCol,
    templateName: templateName.value,
  };

  axios
    .post("/import-project-template/save", template)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          template.templateId = data;
          if (existingTemplateIndex === -1) {
            templates.value.push(template);
          } else {
            templates.value[existingTemplateIndex] = template;
          }
          selectedTemplate.value = template;
          saveTemplateActive.value = false;
          success("Template Saved", "");
        },
        (errors: ApiError[]) => {
          error("Error Saving Template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const tryDeleteTemplate = (): void => {
  confirmPopup(confirm, "Delete Existing Template", `Are you sure you want to delete template ${templateName.value}\?`, deleteTemplate, () =>
    info("Delete Cancelled", "")
  );
};

const deleteTemplate = (): void => {
  if (selectedTemplate.value == null) return;

  axios
    .post("/import-project-template/delete/" + selectedTemplate.value.templateId)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          templates.value.splice(
            templates.value.findIndex((template) => template.templateId === selectedTemplate.value?.templateId),
            1
          );
          selectedTemplate.value = undefined;
          success("Template Deleted", "");
        },
        (errors: ApiError[]) => {
          error("Error Deleting Template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const parseProjectColumn = (templateData: string, colName: string): void => {
  const projectIdValues = templateData.split(":");
  projectFieldData.value[colName + "Tab"] = Number.parseInt(projectIdValues[0]);
  projectFieldData.value[colName + "Row"] = Number.parseInt(projectIdValues[1]);
  projectFieldData.value[colName + "Col"] = projectIdValues[2];
};

const applyTemplate = (): void => {
  if (selectedTemplate.value == null) return;

  projectData.value.borrower = selectedTemplate.value.borrower;
  projectData.value.builder = selectedTemplate.value.builder;
  projectData.value.inspector = selectedTemplate.value.inspector;
  parseProjectColumn(selectedTemplate.value.projectId, "projectId");
  parseProjectColumn(selectedTemplate.value.projectName, "projectName");
  parseProjectColumn(selectedTemplate.value.projectAddress, "projectAddress");
  parseProjectColumn(selectedTemplate.value.projectNotes, "projectNotes");
  parseProjectColumn(selectedTemplate.value.projectInitLenderFunds, "initialLenderFunds");
  parseProjectColumn(selectedTemplate.value.projectAdjLenderFunds, "adjustedLenderFunds");
  parseProjectColumn(selectedTemplate.value.projectInitEquity, "initialOtherEquity");
  parseProjectColumn(selectedTemplate.value.projectAdjEquity, "adjustedOtherEquity");

  budgetData.value.tab = selectedTemplate.value.budgetTab;
  budgetData.value.startRow = selectedTemplate.value.budgetStartRow;
  budgetData.value.endRow = selectedTemplate.value.budgetEndRow;
  budgetFieldData.value.lineRefCol = selectedTemplate.value.budgetLineRef;
  budgetFieldData.value.descriptionCol = selectedTemplate.value.budgetDescription;
  budgetFieldData.value.budgetAmtCol = selectedTemplate.value.budgetAmount;
  budgetFieldData.value.initBudgetAmtCol = selectedTemplate.value.initialBudget;
  budgetFieldData.value.priorDrawnToDateCol = selectedTemplate.value.budgetPriorDrawnToDate;
  budgetFieldData.value.thisDrawAmtCol = selectedTemplate.value.budgetThisDrawAmount;
  budgetFieldData.value.priorEquityDrawnToDateCol = selectedTemplate.value.budgetPriorEquityDrawnToDate;
  budgetFieldData.value.thisEquityDrawAmtCol = selectedTemplate.value.budgetEquityThisDrawAmount;
  budgetFieldData.value.inspPriorDrawCol = selectedTemplate.value.budgetInspPriorDraw;
  budgetFieldData.value.inspThisDrawCol = selectedTemplate.value.budgetInspThisDraw;
};

const resetImport = (): void => {
  if (tbdEntity.value) {
    projectData.value.borrower = tbdEntity.value;
    projectData.value.builder = tbdEntity.value;
    projectData.value.inspector = tbdEntity.value;
  }

  parseProjectColumn("0:0:", "projectId");
  parseProjectColumn("0:0:", "projectName");
  parseProjectColumn("0:0:", "projectAddress");
  parseProjectColumn("0:0:", "projectNotes");
  parseProjectColumn("0:0:", "initialLenderFunds");
  parseProjectColumn("0:0:", "adjustedLenderFunds");
  parseProjectColumn("0:0:", "initialOtherEquity");
  parseProjectColumn("0:0:", "adjustedOtherEquity");

  budgetData.value = ImportBudgetSchema.parse({});
  budgetFieldData.value.lineRefCol = "";
  budgetFieldData.value.descriptionCol = "";
  budgetFieldData.value.budgetAmtCol = "";
  budgetFieldData.value.priorDrawnToDateCol = "";
  budgetFieldData.value.thisDrawAmtCol = "";
  budgetFieldData.value.initBudgetAmtCol = "";
  budgetFieldData.value.priorEquityDrawnToDateCol = "";
  budgetFieldData.value.thisEquityDrawAmtCol = "";
  budgetFieldData.value.inspPriorDrawCol = "";
  budgetFieldData.value.inspThisDrawCol = "";
};

const importProject = (): void => {
  if (file.value == null) {
    error("Invalid Import", "No import file selected");
    return;
  }
  if (projectData.value.selectedUserGroup == null) {
    error("Invalid Import", "Selected user group may not be empty");
    return;
  }
  if (projectData.value.borrower == null) {
    error("Invalid Import", "Borrower may not be empty");
    return;
  }
  if (projectData.value.builder == null) {
    error("Invalid Import", "Builder may not be empty");
    return;
  }
  if (projectData.value.inspector == null) {
    error("Invalid Import", "Inspector may not be empty");
    return;
  }

  setProjectImportInfo(projectData.value, projectFieldData.value);
  setBudgetImportInfo(budgetData.value, budgetFieldData.value);

  //TODO: let csv have tab of 0
  if (projectData.value.projectIdTab === 0 || projectData.value.projectIdCol === -1 || projectData.value.projectIdTab === 0) {
    error("Invalid Import", "You must select a valid import for PROJECT ID");
    return;
  }

  if (projectData.value.projectNameTab === 0 || projectData.value.projectNameCol === -1 || projectData.value.projectNameTab === 0) {
    error("Invalid Import", "You must select a valid import for PROJECT NAME");
    return;
  }

  const data = toFormData2(projectData.value, "projectData");
  toFormData2(budgetData.value, "budgetData", data);
  data.append("file", file.value);

  axios
    .post("/projects/import", data)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          router.push({
            name: "project-dashboard",
            params: {
              id: response.data.result,
            },
          });
        },
        (errorlist: ApiError[]) => {
          for (let e of errorlist) {
            error("Import Error", e.message, 10000);
          }
        }
      );
    })
    .catch((e) => errorApi(e));
};

onBeforeMount(async (): Promise<void> => {
  axios
    .get("/import-project-template")
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          parseData(ImportProjectTemplateListSchema, data, templates);
        },
        (errors: ApiError[]) => {
          error("Cannot retrieve import templates", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));

  getUserGroups(true, true).then((result: Code[]): void => {
    userGroups.value = result;
    projectData.value.selectedUserGroup = userGroups.value[0];
  });

  getAllEntities().then((result: Entity[]): void => {
    entities.value = result;
    tbdEntity.value = entities.value.find((entity: Entity) => entityIsTBD(entity));
    if (tbdEntity.value) {
      projectData.value.borrower = tbdEntity.value;
      projectData.value.builder = tbdEntity.value;
      projectData.value.inspector = tbdEntity.value;
    }
  });
});
</script>
